import { Link } from 'react-router-dom';
import { 
  FileText, Shield, Award, Truck, Thermometer, Package, Zap,
  CheckCircle, ArrowRight, Phone, Download, Mail, Clock
} from 'lucide-react';

const BrokerSetup = () => {
  const carrierInfo = {
    companyName: 'Douala Express Inc.',
    usdot: '2520499',
    mc: '874923',
    phone: '773-344-7748',
    email: 'dispatch.douala@gmail.com',
  };

  const equipmentCapabilities = [
    { icon: Truck, name: 'Dry Van', description: '53\' enclosed trailers' },
    { icon: Thermometer, name: 'Reefer', description: 'Temperature controlled' },
    { icon: Package, name: 'Flatbed', description: 'Open deck trailers' },
    { icon: Zap, name: 'Power-Only', description: 'Your trailer, our power' },
    { icon: Truck, name: 'Box Truck', description: '16-26\' units' },
  ];

  const documents = [
    { name: 'Carrier Packet', description: 'Complete carrier setup documentation', icon: FileText },
    { name: 'W-9 Form', description: 'Tax identification document', icon: FileText },
    { name: 'Certificate of Insurance (COI)', description: 'Current insurance certificate', icon: Shield },
    { name: 'Authority Letter', description: 'MC/USDOT authority documentation', icon: Award },
  ];

  const onboardingSteps = [
    {
      number: '1',
      title: 'Request Documents',
      description: 'Contact our dispatch team to request our carrier packet and supporting documents.',
    },
    {
      number: '2',
      title: 'Complete Setup',
      description: 'Add Douala Express Inc. to your carrier database with our MC and insurance information.',
    },
    {
      number: '3',
      title: 'Start Shipping',
      description: 'Contact dispatch with your load details and we\'ll provide competitive rates.',
    },
  ];

  return (
    <div className="bg-white" data-testid="broker-setup-page">
      {/* Hero Header */}
      <section className="bg-[#0B2A5B] py-20" data-testid="broker-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#FFD700] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
            For Brokers & Shippers
          </span>
          <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight font-['Montserrat'] mb-4">
            Broker & Shipper Setup
          </h1>
          <p className="text-white/70 max-w-2xl mx-auto">
            Everything you need to add Douala Express Inc. to your carrier network. Fast, professional onboarding.
          </p>
        </div>
      </section>

      {/* Carrier Overview */}
      <section className="section-padding" data-testid="carrier-overview">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="text-[#C62828] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
                Carrier Overview
              </span>
              <h2 className="text-3xl font-bold text-[#0B2A5B] tracking-tight mb-6 font-['Montserrat']">
                {carrierInfo.companyName}
              </h2>
              <p className="text-[#6B7280] mb-6 leading-relaxed">
                Douala Express Inc. is a fully licensed and insured interstate motor carrier providing reliable freight transportation services across all 48 continental United States. We specialize in dry van, reefer, flatbed, and expedited services.
              </p>
              
              <div className="bg-[#F8FAFC] p-6 rounded-sm border-l-4 border-[#FFD700]">
                <h4 className="text-sm font-bold text-[#0B2A5B] uppercase tracking-wider mb-4 font-['Montserrat']">
                  Authority Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[#6B7280] text-xs uppercase tracking-wider">USDOT</p>
                    <p className="text-[#0B2A5B] font-bold text-lg">{carrierInfo.usdot}</p>
                  </div>
                  <div>
                    <p className="text-[#6B7280] text-xs uppercase tracking-wider">MC Number</p>
                    <p className="text-[#0B2A5B] font-bold text-lg">{carrierInfo.mc}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#0B2A5B] p-8 rounded-sm">
              <h3 className="text-white font-bold text-lg mb-6 font-['Montserrat']">
                Quick Contact
              </h3>
              <div className="space-y-4">
                <a
                  href={`tel:${carrierInfo.phone}`}
                  className="flex items-center space-x-4 text-white/80 hover:text-[#FFD700] transition-colors"
                  data-testid="broker-phone"
                >
                  <Phone className="h-5 w-5" />
                  <span>{carrierInfo.phone}</span>
                </a>
                <a
                  href={`mailto:${carrierInfo.email}`}
                  className="flex items-center space-x-4 text-white/80 hover:text-[#FFD700] transition-colors"
                  data-testid="broker-email"
                >
                  <Mail className="h-5 w-5" />
                  <span>{carrierInfo.email}</span>
                </a>
                <div className="flex items-center space-x-4 text-white/80">
                  <Clock className="h-5 w-5" />
                  <span>Dispatch Available 24/7</span>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="flex items-center space-x-2 text-[#FFD700]">
                  <Shield className="h-5 w-5" />
                  <span className="text-sm font-semibold">Fully Licensed & Insured</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Equipment Capabilities */}
      <section className="bg-[#F8FAFC] py-16" data-testid="equipment-capabilities">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-[#C62828] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
              What We Haul
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0B2A5B] tracking-tight font-['Montserrat']">
              Equipment Capabilities
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {equipmentCapabilities.map((item, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-sm text-center border border-slate-200 hover:border-[#0B2A5B] transition-colors"
                data-testid={`equipment-cap-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="bg-[#0B2A5B] w-12 h-12 mx-auto flex items-center justify-center rounded-sm mb-4">
                  <item.icon className="h-6 w-6 text-[#FFD700]" />
                </div>
                <h4 className="text-[#0B2A5B] font-bold text-sm font-['Montserrat'] mb-1">
                  {item.name}
                </h4>
                <p className="text-[#6B7280] text-xs">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Available */}
      <section className="section-padding" data-testid="documents-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-[#C62828] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
              Documentation
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0B2A5B] tracking-tight font-['Montserrat']">
              Documents Available Upon Request
            </h2>
            <p className="text-[#6B7280] mt-4 max-w-xl mx-auto">
              Contact dispatch to request any of the following documents for carrier setup.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {documents.map((doc, index) => (
              <div
                key={index}
                className="document-card flex items-start space-x-4"
                data-testid={`document-${doc.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="bg-[#0B2A5B] p-3 rounded-sm flex-shrink-0">
                  <doc.icon className="h-5 w-5 text-[#FFD700]" />
                </div>
                <div>
                  <h4 className="text-[#0B2A5B] font-bold font-['Montserrat'] mb-1">
                    {doc.name}
                  </h4>
                  <p className="text-[#6B7280] text-sm">{doc.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <a
              href={`mailto:${carrierInfo.email}?subject=Document Request - Carrier Setup`}
              className="btn-primary inline-flex items-center space-x-2"
              data-testid="request-documents-btn"
            >
              <Download className="h-4 w-4" />
              <span>Request Documents</span>
            </a>
          </div>
        </div>
      </section>

      {/* 3-Step Onboarding */}
      <section className="bg-[#0B2A5B] py-16" data-testid="onboarding-steps">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#FFD700] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
              Getting Started
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight font-['Montserrat']">
              3-Step Onboarding Process
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {onboardingSteps.map((step, index) => (
              <div
                key={index}
                className="text-center"
                data-testid={`onboarding-step-${step.number}`}
              >
                <div className="step-number mx-auto mb-6 text-2xl">
                  {step.number}
                </div>
                <h3 className="text-white font-bold text-lg mb-3 font-['Montserrat']">
                  {step.title}
                </h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  {step.description}
                </p>
                {index < onboardingSteps.length - 1 && (
                  <div className="hidden md:block absolute top-8 right-0 transform translate-x-1/2">
                    <ArrowRight className="h-6 w-6 text-[#FFD700]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-banner py-16" data-testid="broker-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 font-['Montserrat']">
            Ready to Get Started?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Contact our dispatch team today to request documents and start shipping with Douala Express Inc.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href={`mailto:${carrierInfo.email}?subject=Carrier Setup Request`}
              className="bg-white text-[#C62828] px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-slate-100 transition-all shadow-lg inline-flex items-center justify-center space-x-2"
              data-testid="broker-cta-email"
            >
              <Mail className="h-4 w-4" />
              <span>Email Dispatch</span>
            </a>
            <a
              href={`tel:${carrierInfo.phone}`}
              className="border-2 border-white text-white px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-white hover:text-[#C62828] transition-all inline-flex items-center justify-center space-x-2"
              data-testid="broker-cta-call"
            >
              <Phone className="h-4 w-4" />
              <span>Call Now</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BrokerSetup;
